import { useTranslations } from 'next-intl';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '@/app/components/ui/use-toast';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ImageUploadFile, ImageUploadFileInfo } from '@/app/types';
import { uploadFile } from '@/app/utils/client';
import { useGlobalModal } from './use-global-modal';
import type { ImageValidatorResult } from './use-image-validator';

interface FileUploadHookOptions {
  onChange: (file: ImageUploadFile, fileUploadInfo?: ImageUploadFileInfo) => void;
  validateImage: (file: File) => Promise<ImageValidatorResult>;
  isInitialSelectedImageRange: boolean;
  scene?: UploadScene; // 上传场景，社区用 'community'，主体用 'material'，默认为 'vidu'
  getSelectedImage?: (url: string) => Promise<{ blob: Blob; width: number; height: number } | null>;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
  emitUploadingInTempMode?: boolean; // 临时模式下是否仍然先发出 uploading 事件
  emitUploadingPreview?: boolean; // 图片处理出本地预览后，上传完成前再发一次带 file 的 uploading 事件
  /**
   * 全局 confirmDialog（AuditSubmitIllegal 时弹出）的视觉主题。
   * 不传/'default' = 全站默认黑色风；'admission' = h5/thu-2026-freshmen 紫色风。
   */
  dialogTheme?: 'default' | 'admission';
}

/**
 * 处理图片文件上传的 Hook - 组合文件处理和上传逻辑
 */
export const useFileUpload = ({
  onChange,
  getSelectedImage,
  validateImage,
  isInitialSelectedImageRange,
  scene = UPLOAD_SCENE.VIDU,
  allowTempMode = false,
  emitUploadingInTempMode = true,
  emitUploadingPreview = false,
  dialogTheme,
}: FileUploadHookOptions) => {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const userInfo = useStore((state) => state.userInfo);
  /**
   * 处理文件验证和预处理
   */
  const processFile = async (
    file: File,
  ): Promise<
    | {
        imageURL: string;
        imageResolution: { width: number; height: number };
        selectedImage?: { blob: Blob; width: number; height: number } | null;
      }
    | { error: string; reason: string }
  > => {
    // 验证图片
    const validationResult = await validateImage(file);

    if (!validationResult.valid) {
      const errorMessage = validationResult.errorMessage || 'Unknown error';
      const errorReason = validationResult.errorReason || 'unknown';
      toast({ type: 'error', message: errorMessage });
      return { error: errorMessage, reason: errorReason };
    }

    const { imageURL, imageResolution } = validationResult;

    try {
      if (getSelectedImage) {
        // 获取选中的图片区域
        const selectedImage = await getSelectedImage(imageURL);
        return {
          imageURL,
          imageResolution,
          selectedImage,
        };
      }
      return {
        imageURL,
        imageResolution,
      };
    } catch {
      const errorMessage = t('popup.toast.uploadingError');
      toast({ type: 'error', message: errorMessage });
      return { error: errorMessage, reason: 'processingError' };
    }
  };

  /**
   * 创建初始上传信息
   */
  const createUploadInfo = (): ImageUploadFileInfo => ({
    image_resolution: '',
    pic_type: '',
    is_success: false,
    fail_reason: '',
    upload_time: 0,
  });

  /**
   * 处理完整的文件上传流程
   */
  const processFileUpload = async (file: File, batchIndex = 0) => {
    const id = uuidv4();
    const fileUploadInfo = createUploadInfo();
    const startUploadTime = performance.now();
    const isTempMode = allowTempMode && !userInfo;

    if (!isTempMode || emitUploadingInTempMode) {
      onChange({
        id,
        file: null,
        status: 'uploading',
        batchIndex,
      });
    }

    // 1. 处理文件（验证 + 预处理）
    const processedResult = await processFile(file);

    if ('error' in processedResult) {
      fileUploadInfo.fail_reason = processedResult.reason;
      handleUploadError({ id, fileUploadInfo, showToast: false });
      return;
    }

    const { imageURL, imageResolution, selectedImage } = processedResult;
    fileUploadInfo.image_resolution = `${imageResolution.width}x${imageResolution.height}`;
    if (emitUploadingPreview) {
      onChange({
        id,
        file: {
          sourceImage: '',
          sourceImagePreview: imageURL,
          sourceImageWidth: imageResolution.width,
          sourceImageHeight: imageResolution.height,
          selectedImage: '',
          selectedImagePreview: imageURL,
          selectedImageWidth: selectedImage?.width ?? imageResolution.width,
          selectedImageHeight: selectedImage?.height ?? imageResolution.height,
        },
        status: 'uploading',
        batchIndex,
      });
    }

    // 2. 执行上传（临时模式跳过实际上传）
    let sourceImageResult: Awaited<ReturnType<typeof uploadFile>> | null = null;
    let resizeImageResult: Awaited<ReturnType<typeof uploadFile>> | null = null;

    if (isTempMode) {
      // 临时模式：创建临时图片对象，不上传到服务端
      sourceImageResult = {
        uri: '', // 临时模式下没有服务端 URI
        file: file, // 保存原始文件用于后续上传
        width: imageResolution.width,
        height: imageResolution.height,
      };

      if (selectedImage) {
        resizeImageResult = {
          uri: '',
          file: selectedImage.blob,
          width: selectedImage.width,
          height: selectedImage.height,
        };
      }
    } else {
      // 正常模式：实际上传到服务端
      const uploadTasks = [
        uploadFile({ file, width: imageResolution.width, height: imageResolution.height, scene }),
        ...(selectedImage
          ? [uploadFile({ file: selectedImage.blob, width: selectedImage.width, height: selectedImage.height, scene })]
          : []),
      ];

      const results = await Promise.all(uploadTasks);
      sourceImageResult = results[0];
      resizeImageResult = results[1] || null;
    }

    // 取消上传，不处理错误，直接返回（仅正常模式）
    if (!isTempMode) {
      if (sourceImageResult?.cancelled || resizeImageResult?.cancelled) {
        return;
      }

      // 检查上传错误
      if (!sourceImageResult || sourceImageResult.error) {
        fileUploadInfo.fail_reason = sourceImageResult?.error?.reason || 'uploadingError';
        handleUploadError({ id, fileUploadInfo, auditId: sourceImageResult?.error?.metadata?.audit_id });
        return;
      }

      if (resizeImageResult?.error) {
        fileUploadInfo.fail_reason = resizeImageResult.error.reason;
        handleUploadError({ id, fileUploadInfo, auditId: resizeImageResult.error.metadata?.audit_id });
        return;
      }
    }

    // 使用实际上传的宽高（如果进行了压缩，则为压缩后的宽高）
    const actualWidth = sourceImageResult?.width ?? imageResolution.width;
    const actualHeight = sourceImageResult?.height ?? imageResolution.height;

    // 3. 更新成功状态
    fileUploadInfo.is_success = !isTempMode; // 临时模式下标记为未成功上传
    fileUploadInfo.upload_time = performance.now() - startUploadTime;
    fileUploadInfo.image_resolution = `${actualWidth}x${actualHeight}`;

    // 如果图片被压缩了，使用压缩后的文件创建预览图
    // 临时模式：使用本地预览 URL
    const sourceImagePreview = isTempMode
      ? imageURL
      : sourceImageResult?.width !== undefined && sourceImageResult.width !== imageResolution.width
        ? URL.createObjectURL(sourceImageResult.file)
        : imageURL;

    // 临时模式：使用临时标识符，保存文件对象用于后续上传
    const tempFileData = isTempMode
      ? {
          _tempFile: file, // 保存原始文件
          _tempSelectedImage: selectedImage?.blob, // 保存裁剪后的图片
        }
      : {};

    onChange?.(
      {
        id,
        file: {
          sourceImage: isTempMode ? `temp://${id}` : sourceImageResult?.uri || '',
          sourceImagePreview,
          sourceImageWidth: actualWidth,
          sourceImageHeight: actualHeight,
          selectedImageRange: isInitialSelectedImageRange ? [0, 0, actualWidth, actualHeight] : undefined,
          ...(selectedImage && resizeImageResult
            ? {
                selectedId: isTempMode ? `temp-selected-${id}` : (resizeImageResult.id ?? ''),
                selectedImage: isTempMode ? `temp://selected-${id}` : resizeImageResult.uri,
                selectedImagePreview: URL.createObjectURL(selectedImage.blob),
                selectedImageWidth: selectedImage.width,
                selectedImageHeight: selectedImage.height,
              }
            : {
                selectedImage: isTempMode ? `temp://${id}` : sourceImageResult?.uri || '',
                selectedImagePreview: sourceImagePreview,
                selectedImageWidth: actualWidth,
                selectedImageHeight: actualHeight,
              }),
          ...tempFileData,
        },
        status: 'done',
      },
      fileUploadInfo,
    );
  };

  /**
   * 处理上传错误
   */
  const handleUploadError = ({
    id,
    fileUploadInfo,
    showToast = true,
    auditId,
  }: {
    id: string;
    fileUploadInfo: ImageUploadFileInfo;
    showToast?: boolean;
    auditId?: string;
  }) => {
    if (showToast) {
      if (fileUploadInfo.fail_reason === 'AuditSubmitIllegal') {
        const message = t('create.createForm.upload.error.AuditSubmitIllegal');
        openConfirmDialog({
          title: t('popup.dialog.error.checkFailed.title'),
          content: auditId ? `${message}\n(ID: ${auditId})` : message,
          primaryButtonText: t('popup.dialog.error.confirm'),
          theme: dialogTheme,
        });
      } else {
        toast({ type: 'error', message: t('popup.toast.uploadingError') });
      }
    }

    onChange(
      {
        id,
        file: null,
        status: 'error',
      },
      fileUploadInfo,
    );
  };

  return { processFileUpload };
};
